/**
 * MSAL (Microsoft Authentication Library) Configuration
 *
 * This file handles the configuration and setup for Microsoft Azure authentication
 * in the LexAI Dashboard. It provides secure authentication flow using OAuth 2.0
 * and OpenID Connect protocols.
 *
 * Security Features:
 * - Dynamic configuration loading from backend (no hardcoded secrets)
 * - Session storage for token caching (recommended for SPAs)
 * - PII logging disabled for compliance
 * - Secure cookie settings
 *
 * Architecture:
 * - Default config serves as fallback during initialization
 * - Real config loaded dynamically from backend API endpoint
 * - Minimal required scopes for dashboard functionality
 */

import { Configuration, PopupRequest } from "@azure/msal-browser";

/**
 * Gets the default redirect URI based on environment and deployment context
 * @returns Default redirect URI string
 */
const getDefaultRedirectUri = (): string => {
  // Use environment variable if set, otherwise use current origin
  if (process.env.REACT_APP_REDIRECT_URI) {
    return process.env.REACT_APP_REDIRECT_URI;
  }

  // In production, use the current page origin (works for any deployment URL)
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Development fallback
  return "http://localhost:3000";
};

/**
 * Default MSAL Configuration
 *
 * This serves as the initial/fallback configuration before dynamic loading.
 * In production, this config is replaced by values fetched from the backend
 * to avoid exposing sensitive configuration in client-side code.
 *
 * Security Notes:
 * - navigateToLoginRequestUrl: false prevents redirect-based attacks
 * - storeAuthStateInCookie: false avoids CSRF vulnerabilities in SPAs
 * - secureCookies: true ensures cookies are only sent over HTTPS
 * - piiLoggingEnabled: false prevents accidental logging of personal data
 */
export let msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || "loading", // Use env var if available
    authority: process.env.REACT_APP_AZURE_AUTHORITY || "https://login.microsoftonline.com/common",
    redirectUri: process.env.REACT_APP_REDIRECT_URI || getDefaultRedirectUri(),
    postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI || getDefaultRedirectUri(),
    navigateToLoginRequestUrl: true, // Navigate back to clean URL after auth redirect
  },
  cache: {
    cacheLocation: "sessionStorage", // Security: Tokens cleared when browser tab closes
    storeAuthStateInCookie: false, // Security: Prevent CSRF in SPAs
    secureCookies: process.env.NODE_ENV === 'production', // Only secure in production
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return; // Security: Never log personally identifiable information
        // Only log MSAL messages in development mode for debugging
        if (process.env.NODE_ENV === 'development') {
          console.debug('[MSAL]', message);
        }
      },
      piiLoggingEnabled: false, // Security: Explicitly disable PII logging
    },
    // Improved popup handling and network resilience
    windowHashTimeout: 60000, // 60 seconds for popup operations
    iframeHashTimeout: 6000,   // 6 seconds for iframe operations
    loadFrameTimeout: 0,       // No timeout for frame loading to prevent premature failures
  },
};

/**
 * Dynamic MSAL Configuration Loader
 *
 * This function securely loads MSAL configuration from the backend API endpoint.
 * This approach prevents exposing sensitive authentication configuration
 * (like client secrets) in client-side code.
 *
 * Security Benefits:
 * - No hardcoded secrets in frontend bundle
 * - Configuration can be changed without redeploying frontend
 * - Backend validates and sanitizes configuration before sending
 *
 * API Endpoint: GET /api/auth/config
 * Expected Response: { clientId: string, tenantId: string, authority: string }
 *
 * @returns Promise<Configuration> - Updated MSAL configuration object
 *
 * @example
 * ```json
 * // Backend API Response (GET /api/auth/config)
 * {
 *   "clientId": "12345678-1234-1234-1234-123456789012",
 *   "tenantId": "87654321-4321-4321-4321-210987654321",
 *   "authority": "https://login.microsoftonline.com/87654321-4321-4321-4321-210987654321",
 *   "redirectUri": "https://lexai.prosperfinance.com/auth/callback"
 * }
 *
 * // MSAL Configuration Object (Returned)
 * {
 *   "auth": {
 *     "clientId": "12345678-1234-1234-1234-123456789012",
 *     "authority": "https://login.microsoftonline.com/87654321-4321-4321-4321-210987654321",
 *     "redirectUri": "https://lexai.prosperfinance.com/auth/callback"
 *   },
 *   "cache": {
 *     "cacheLocation": "sessionStorage"
 *   }
 * }
 * ```
 */
export const updateMsalConfig = async (): Promise<Configuration> => {
  try {
    console.log('🔧 Loading MSAL configuration from backend...');

    // Environment-aware backend URL configuration
    const getBackendUrls = (): string[] => {
      // Priority order: env var -> localhost (browser-accessible) -> Docker service -> fallback IPs
      const envUrl = process.env.REACT_APP_BACKEND_URL;
      if (envUrl) {
        return [
          `${envUrl}/api/auth/config`,
          'http://localhost:8080/api/auth/config',
          'http://lexai-backend:8080/api/auth/config',
          'http://172.18.0.4:8080/api/auth/config'
        ];
      }

      // Default configuration for different environments
      // Prioritize localhost as it's directly accessible from browser
      // Docker service names only work from within Docker network
      return [
        'http://localhost:8080/api/auth/config',     // Localhost (browser-accessible)
        'http://lexai-backend:8080/api/auth/config', // Docker service name (only works within Docker network)
        'http://172.18.0.4:8080/api/auth/config'     // Direct IP fallback
      ];
    };

    const backendUrls = getBackendUrls();
    let response: Response | null = null;
    let lastError: Error | null = null;
    let successUrl: string | null = null;

    // Try URLs with exponential backoff for network resilience
    for (let attempt = 0; attempt < backendUrls.length; attempt++) {
      const url = backendUrls[attempt];
      try {
        console.log(`🔧 Attempting backend URL (${attempt + 1}/${backendUrls.length}): ${url}`);

        // Create AbortController for timeout with progressive delay
        const controller = new AbortController();
        const timeoutMs = Math.min(5000 + (attempt * 2000), 15000); // Progressive timeout: 5s, 7s, 9s, max 15s
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        response = await fetch(url, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          console.log(`✅ Successfully connected to backend at: ${url}`);
          successUrl = url;
          break;
        } else {
          console.warn(`⚠️ Backend responded with status: ${response.status} at ${url}`);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        console.warn(`⚠️ Failed to connect to ${url}: ${errorMsg}`);
        lastError = error as Error;

        // If it's a network error and we have more URLs, continue to next
        if (attempt < backendUrls.length - 1) {
          console.log(`🔄 Trying next URL...`);
        }
      }
    }

    if (!response || !response.ok) {
      console.error('❌ All backend URLs failed, using environment-based fallback configuration');

      // Enhanced fallback configuration with environment variables and better error context
      const fallbackConfig = {
        auth: {
          clientId: process.env.REACT_APP_AZURE_CLIENT_ID || "loading",
          authority: process.env.REACT_APP_AZURE_AUTHORITY || "https://login.microsoftonline.com/common",
          redirectUri: process.env.REACT_APP_REDIRECT_URI || getDefaultRedirectUri(),
          postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI || getDefaultRedirectUri(),
          navigateToLoginRequestUrl: false
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
          secureCookies: process.env.NODE_ENV === 'production'
        },
        system: {
          loggerOptions: {
            loggerCallback: (level: any, message: string, containsPii: boolean) => {
              if (containsPii) return;
              if (process.env.NODE_ENV === 'development') {
                console.debug('[MSAL Fallback]', message);
              }
            },
            piiLoggingEnabled: false
          }
        }
      };

      msalConfig = fallbackConfig;

      // Log configuration status for debugging
      const hasClientId = process.env.REACT_APP_AZURE_CLIENT_ID && process.env.REACT_APP_AZURE_CLIENT_ID !== "loading";
      console.log(`⚠️ Using fallback MSAL configuration:
        - Client ID: ${hasClientId ? '✅ Set via env' : '❌ Not set - using placeholder'}
        - Authority: ${process.env.REACT_APP_AZURE_AUTHORITY || 'Default (common)'}
        - Redirect URI: ${process.env.REACT_APP_REDIRECT_URI || getDefaultRedirectUri()}

        To fix this, ensure REACT_APP_AZURE_CLIENT_ID is set in your environment variables.`);

      return msalConfig;
    }

    // Parse and validate the configuration response
    const config = await response.json();

    // Security: Validate response structure before using
    if (!config || typeof config !== 'object') {
      throw new Error('Invalid configuration response format');
    }

    // Check for required configuration properties
    if (config.clientId && config.tenantId) {
      // Create minimal MSAL config with only required settings
      // This reduces attack surface by excluding optional/unnecessary settings
      const redirectUri = getDefaultRedirectUri();
      msalConfig = {
        auth: {
          clientId: config.clientId, // Azure App Registration client ID
          authority: config.authority, // Azure AD authority URL
          redirectUri: redirectUri,
          postLogoutRedirectUri: redirectUri,
          navigateToLoginRequestUrl: true, // Clean URL after auth redirect
        },
        cache: {
          cacheLocation: "sessionStorage", // Security: Tokens cleared when browser tab closes
          storeAuthStateInCookie: false,
          secureCookies: process.env.NODE_ENV === 'production'
        }
      };
      console.log(`✅ MSAL configuration loaded successfully (redirect: ${redirectUri})`);
    } else {
      console.warn('⚠️ Invalid MSAL configuration from backend - using defaults');
    }
  } catch (error) {
    console.error('❌ Failed to load MSAL configuration:', error);
    // Falls back to default configuration defined above
  }

  return msalConfig;
};

/**
 * Authentication Request Configuration
 *
 * Defines the OAuth scopes required for the dashboard's functionality.
 * These scopes determine what Microsoft Graph API permissions the application requests.
 *
 * Security Principle: Request only the minimum required permissions (principle of least privilege)
 *
 * Current Scopes:
 * - User.Read: Access basic user profile information
 * - Mail.Read: Read user's email messages
 * - Tasks.ReadWrite: Read and manage user's tasks (Microsoft To Do/Planner)
 * - Calendars.Read: Read user's calendar events (backup for task functionality)
 *
 * Note: These scopes must be configured in the Azure App Registration
 * and granted admin consent for the organization.
 *
 * @example
 * ```json
 * // OAuth 2.0 Authorization Request (Popup Flow)
 * {
 *   "scopes": [
 *     "User.Read",
 *     "Mail.Read",
 *     "Tasks.ReadWrite",
 *     "Calendars.Read"
 *   ],
 *   "popupWindowFeatures": {
 *     "width": 483,
 *     "height": 600
 *   },
 *   "prompt": "select_account"
 * }
 *
 * // Microsoft Graph API Permissions Required
 * {
 *   "User.Read": {
 *     "description": "Sign you in and read your profile",
 *     "type": "Delegated",
 *     "adminConsentRequired": false
 *   },
 *   "Mail.Read": {
 *     "description": "Read your mail",
 *     "type": "Delegated",
 *     "adminConsentRequired": false
 *   },
 *   "Tasks.ReadWrite": {
 *     "description": "Read and write your tasks and task lists",
 *     "type": "Delegated",
 *     "adminConsentRequired": false
 *   },
 *   "Calendars.Read": {
 *     "description": "Read your calendars",
 *     "type": "Delegated",
 *     "adminConsentRequired": false
 *   }
 * }
 * ```
 */
/**
 * Backend API scope for OBO (On-Behalf-Of) flow
 * The backend will exchange this token for Graph API tokens
 * This is more secure than requesting Graph tokens directly
 *
 * IMPORTANT: For OBO flow, the frontend should ONLY request the backend API scope.
 * Graph permissions are configured in Azure AD > API Permissions and the backend
 * exchanges the user token for Graph tokens via the OBO flow.
 */
const getBackendApiScope = (): string => {
  // Use environment variable if set, otherwise construct from client ID
  if (process.env.REACT_APP_BACKEND_API_SCOPE) {
    return process.env.REACT_APP_BACKEND_API_SCOPE;
  }
  // Fallback: construct scope from client ID
  const clientId = process.env.REACT_APP_AZURE_CLIENT_ID || msalConfig.auth.clientId;
  if (clientId && clientId !== 'loading') {
    return `api://${clientId}/access_as_user`;
  }
  // Final fallback: empty string triggers direct Graph scope mode
  return '';
};

/**
 * Graph API scopes - ONLY used as fallback when OBO is not configured.
 * When OBO is properly configured, Graph permissions are granted through
 * Azure AD API Permissions, not through the frontend token request.
 */
const graphScopesFallback = [
  "User.Read",            // User profile information for personalization
  "Mail.Read",            // Email access for triage and processing
  "Tasks.ReadWrite",      // Task management (Microsoft To Do/Planner integration)
  "Calendars.Read",       // Calendar events (alternative task source)
  "Files.ReadWrite.All",  // SharePoint/OneDrive file write access for client.md sync
  "Sites.ReadWrite.All"   // SharePoint site document library access for sandbox
];

export const loginRequest: PopupRequest = {
  scopes: (() => {
    const backendScope = getBackendApiScope();
    if (backendScope) {
      // OBO flow: Frontend ONLY requests the backend API scope.
      // Graph permissions are granted via admin consent in Azure Portal
      // and the backend exchanges the user token for Graph tokens via OBO.
      //
      // This avoids AADSTS90094 when admin consent policy blocks user-initiated
      // consent for Graph scopes (Files.ReadWrite.All, Sites.ReadWrite.All, etc.)
      //
      // Admin consent URL: https://login.microsoftonline.com/{tenantId}/adminconsent?client_id={clientId}
      console.log('🔐 Using OBO flow - requesting backend API scope only:', backendScope);
      return [backendScope, 'openid', 'profile', 'offline_access'];
    }
    // Fallback: direct Graph scopes (when backend API scope not configured)
    console.warn('⚠️ OBO not configured - using direct Graph scopes. Set REACT_APP_BACKEND_API_SCOPE for proper OBO flow.');
    return graphScopesFallback;
  })()
};

/**
 * Microsoft Graph API Endpoints
 *
 * Defines the REST API endpoints used by the dashboard to interact with Microsoft services.
 * These endpoints are part of the Microsoft Graph API which provides unified access
 * to Microsoft 365 services.
 *
 * Available Endpoints:
 * - /me: Current authenticated user information
 * - /me/messages: User's email messages
 * - /me/planner/tasks: User's tasks from Microsoft Planner
 *
 * Note: All requests to these endpoints require valid authentication tokens
 * obtained through the MSAL authentication flow.
 *
 * @example
 * ```json
 * // User Profile Endpoint (/me)
 * {
 *   "@odata.context": "https://graph.microsoft.com/v1.0/$metadata#users/$entity",
 *   "id": "12345678-1234-1234-1234-123456789012",
 *   "displayName": "John Doe",
 *   "givenName": "John",
 *   "surname": "Doe",
 *   "userPrincipalName": "john.doe@prosperfinance.com",
 *   "mail": "john.doe@prosperfinance.com",
 *   "jobTitle": "Senior Loan Officer",
 *   "department": "Mortgage Services",
 *   "officeLocation": "Sydney Office"
 * }
 *
 * // Email Messages Endpoint (/me/messages)
 * {
 *   "@odata.context": "https://graph.microsoft.com/v1.0/$metadata#users('user-id')/messages",
 *   "value": [
 *     {
 *       "id": "AAMkADhlMzJmNzVjLTQ1ZGQtNGI5ZS1hNzU4LTY0MjEwOWI1ZjE5OQBGAAAAAADdNpHnV7Z4RK1V8vU6WnJYAQCfOJ5xgkZrRZ5VzHnK5QwAAAAAAEMAACfOJ5xgkZrRZ5VzHnK5QwAAAEJvAAA=",
 *       "subject": "Mortgage Application Inquiry",
 *       "sender": {
 *         "emailAddress": {
 *           "name": "Jane Smith",
 *           "address": "jane.smith@email.com"
 *         }
 *       },
 *       "receivedDateTime": "2024-01-17T10:30:00Z",
 *       "isRead": false,
 *       "hasAttachments": true
 *     }
 *   ]
 * }
 *
 * // Planner Tasks Endpoint (/me/planner/tasks)
 * {
 *   "@odata.context": "https://graph.microsoft.com/v1.0/$metadata#users('user-id')/planner/tasks",
 *   "value": [
 *     {
 *       "id": "abc123-def456-ghi789",
 *       "title": "Review loan application",
 *       "createdDateTime": "2024-01-15T09:00:00Z",
 *       "dueDateTime": "2024-01-20T17:00:00Z",
 *       "percentComplete": 0,
 *       "priority": 5,
 *       "bucketId": "bucket-123",
 *       "planId": "plan-456"
 *     }
 *   ]
 * }
 * ```
 */
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
  graphMailEndpoint: "https://graph.microsoft.com/v1.0/me/messages",
  graphTasksEndpoint: "https://graph.microsoft.com/v1.0/me/planner/tasks",
};
